import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Modules
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

// App
import { apiEndpoints, serverUrl } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { decodeHtmlEntities } from "../../utils/helpers";
import Pagination from "../../partials/pagination";
import NoData from "../../partials/noData";
import Error from "../../partials/error";
import CompanyappLoader from "../../partials/companyappLoader";
import FavouriteBtn from "../../partials/favouriteBtn";
import { alertMessages } from "../../partials/alertMessages";

// UI
import { Container, Row, Col } from "react-bootstrap";
import {
  Typography,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import GridItem from "../../partials/gridItem";
import PageHeader from "../../partials/pageHeader";

// Every content type the bookmark store can hold ("everything favoritable").
// `value` is the stored contentType; `label` is the human name shown in the
// filter and on each card's type chip.
const TYPE_OPTIONS = [
  { value: "news", label: "News" },
  { value: "blogs", label: "Blog" },
  { value: "event", label: "Event" },
  { value: "presentation", label: "Presentation" },
  { value: "video", label: "Video" },
  { value: "gallery", label: "Gallery" },
  { value: "podcast", label: "Podcast" },
  { value: "document", label: "Document" },
  { value: "course", label: "Training" },
  { value: "quiz", label: "Quiz" },
  { value: "link", label: "Link" },
  { value: "poll", label: "Poll" },
  { value: "webform", label: "Form" },
  { value: "forum", label: "Forum" },
  { value: "company_directory", label: "Directory" },
  { value: "people", label: "User" },
];

const TYPE_LABELS = TYPE_OPTIONS.reduce((map, option) => {
  map[option.value] = option.label;
  return map;
}, {});

// Internal destination per content type. The bookmark button rarely stores a
// `url`, so the card link is normally rebuilt here from contentType + id.
// External snapshot URLs (links, blogs) take precedence when present.
const ROUTE_BY_TYPE = {
  news: (id) => `/news/view/${id}`,
  document: (id) => `/documents/view/${id}`,
  video: (id) => `/videos/view/${id}`,
  podcast: (id) => `/podcasts/view/${id}`,
  event: (id) => `/events/view/${id}`,
  gallery: (id) => `/galleries/view/${id}`,
  quiz: (id) => `/quizzes/view/${id}`,
  poll: (id) => `/polls/view/${id}`,
  presentation: (id) => `/presentations/view/${id}`,
  webform: (id) => `/forms/view/${id}`,
  course: (id) => `/training/view/${id}`,
  company_directory: (id) => `/company_directory/view/${id}`,
  people: (id) => `/contacts/view/${id}`,
  blogs: (id) => `/blogs/${id}`,
};

// Display order of the accordion sections. A section only renders if at
// least one result on the current page matches one of its types.
const SECTIONS = [
  { key: "news", title: "Latest News", types: ["news"] },
  { key: "blogs", title: "Company Blogs", types: ["blogs"] },
  { key: "events", title: "Upcoming Events", types: ["event"] },
  { key: "presentations", title: "Presentations", types: ["presentation"] },
  { key: "videos", title: "Videos", types: ["video"] },
  { key: "galleries", title: "Galleries", types: ["gallery"] },
  { key: "podcasts", title: "Podcasts", types: ["podcast"] },
  { key: "documents", title: "Documents", types: ["document"] },
  { key: "training", title: "Training Courses", types: ["course"] },
  { key: "quizzes", title: "Tests & Quizzes", types: ["quiz"] },
  { key: "links", title: "Useful Links", types: ["link"] },
  { key: "polls", title: "Polls", types: ["poll"] },
  { key: "forms", title: "Surveys & Forms", types: ["webform"] },
  { key: "directory", title: "Directory", types: ["company_directory"] },
  { key: "person", title: "Person", types: ["people"] },
];

const SECTIONED_TYPES = SECTIONS.flatMap((s) => s.types);

const pageSize = 24;

export default function Bookmarks() {
  const user = useSelector((state) => state).authReducer.user;
  // Re-fetch when the saved-items store changes so un-bookmarking a card (via
  // its FavouriteBtn) drops it live.
  const savedItems = useSelector((state) => state).favoritesReducer;

  const [results, setResults] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedType, setSelectedType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  // Drop stale responses so an in-flight page/filter change can't be clobbered
  // by a slower earlier request.
  const requestSeq = useRef(0);

  // Group the current page of results into the ordered accordion sections.
  // Empty sections are filtered out so they never render.
  const sections = useMemo(() => {
    const grouped = SECTIONS.map((section) => ({
      ...section,
      items: results.filter((item) => section.types.includes(item.contentType)),
    }));

    // Catch-all so types not in the list above (e.g. forum) don't vanish.
    const other = results.filter(
      (item) => !SECTIONED_TYPES.includes(item.contentType),
    );
    if (other.length) {
      grouped.push({ key: "other", title: "Other", types: [], items: other });
    }

    return grouped.filter((section) => section.items.length > 0);
  }, [results]);

  const _setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);
    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  const getContent = () => {
    setIsError(false);
    setIsLoading(true);
    const seq = ++requestSeq.current;

    const params = new URLSearchParams();
    params.set("items_per_page", pageSize);
    params.set("page", currentPage);
    if (selectedType !== "all") params.set("types", selectedType);
    if (query) params.set("q", query);

    const path = `${apiEndpoints.bookmarksList}?${params.toString()}`;

    getOnlineNode(path, user.access_token)
      .then((response) => {
        console.log("response", response);
        if (seq !== requestSeq.current) return;
        setResults(response.data?.rows || []);
        setTotalItems(response.data?.pager?.total_items || 0);
        setIsLoading(false);
      })
      .catch((_error) => {
        if (seq !== requestSeq.current) return;
        if (_error.response) {
          _setError(true, _error.response.status, _error.response.statusText);
        } else if (_error.request) {
          _setError(true, 0, alertMessages.requestError.message);
        } else {
          _setError(true, 0, alertMessages.unkownError.message);
        }
      });
  };

  // Fetch on filter/page changes and whenever the store mutates (add/remove).
  useEffect(() => {
    getContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedType, query, savedItems]);

  const applySearch = () => {
    setCurrentPage(0);
    setQuery(searchTerm.trim());
  };

  const handleTypeChange = (event) => {
    setCurrentPage(0);
    setSelectedType(event.target.value);
  };

  const thumbSrc = (item) => {
    if (!item.thumbnail) return "";
    return item.thumbnail.startsWith("http")
      ? item.thumbnail
      : `${serverUrl}${item.thumbnail}`;
  };

  const isExternal = (url) => /^https?:\/\//i.test(url);

  // Resolve where a bookmark card links to: an external snapshot URL if it has
  // one, otherwise the internal route for its content type. Null = not linkable.
  const resolveLink = (item) => {
    if (item.url && isExternal(item.url)) {
      return { href: item.url, external: true };
    }
    const builder = ROUTE_BY_TYPE[item.contentType];
    if (builder) {
      return { href: builder(item.id), external: false };
    }
    if (item.url) {
      return { href: item.url, external: false };
    }
    return null;
  };

  const renderCard = (item) => {
    const image = thumbSrc(item);
    const typeLabel = TYPE_LABELS[item.contentType] || item.contentType;
    const link = resolveLink(item);
    const inner = (
      <div className="bookmark-card-redactor d-flex flex-column w-100 h-100">
        <div className="bookmark-thumbnail-container ratio ratio-16x9">
          {image ? (
            <img
              src={image}
              alt={`${item.title}-thumbnail`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.parentNode?.classList.add(
                  "bookmark-thumbnail-container--empty",
                );
              }}
            />
          ) : (
            <div className="bookmark-thumbnail-placeholder d-flex align-items-center justify-content-center w-100 h-100">
              <Typography owner="client" variant="caption">
                {typeLabel}
              </Typography>
            </div>
          )}
        </div>
        <div className="bookmark-card-body d-flex flex-column">
          <Chip
            className="bookmark-type-chip align-self-start"
            owner="client"
            size="small"
            label={typeLabel}
          />
          <Typography
            className="bookmark-card-title"
            owner="client"
            variant="subtitle1"
            weight="medium"
            color="clientHeading.items"
          >
            {decodeHtmlEntities(item.title || `${typeLabel} ${item.id}`)}
          </Typography>
        </div>
      </div>
    );

    return (
      // <Col
      //   xs={6}
      //   md={4}
      //   lg={3}
      //   key={`bookmark-${item.contentType}-${item.id}`}
      //   className="bookmark-item"
      // >
      //   <div className="bookmark-card item h-100">
      //     <div className="bookmark-card-actions">
      //       <FavouriteBtn
      //         id={item.id}
      //         contentType={item.contentType}
      //         title={item.title}
      //         thumbnail={item.thumbnail}
      //         url={item.url}
      //       />
      //     </div>
      //     {link ? (
      //       link.external ? (
      //         <a
      //           href={link.href}
      //           target="_blank"
      //           rel="noopener noreferrer"
      //           className="bookmark-card-link"
      //         >
      //           {inner}
      //         </a>
      //       ) : (
      //         <Link to={link.href} className="bookmark-card-link">
      //           {inner}
      //         </Link>
      //       )
      //     ) : (
      //       <div className="bookmark-card-link">{inner}</div>
      //     )}
      //   </div>
      // </Col>
      <GridItem
        xs={12}
        sm={6}
        md={3}
        id={item.id}
        itemClass="bookmark"
        title={item.title}
        hasImg={true}
        imgSrc={item.thumbnail}
        cardActionArea={true}
        cardActionCallBack={() => {
          if (link.external) {
          } else {
            navigate(link.href);
          }
        }}
        contentType={item.contentType}
        disableChip={true}
      />
    );
  };

  const renderPagination = () => {
    if (totalItems <= pageSize) return null;
    return (
      <Pagination
        owner="client"
        containerClasses="bookmarks-pagination d-flex justify-content-center align-items-center mt-4"
        previousDisabled={currentPage === 0}
        nextDisabled={results.length < pageSize}
        currentPage={currentPage + 1}
        onPrevious={() => {
          setIsLoading(true);
          setCurrentPage(currentPage - 1);
        }}
        onNext={() => {
          setIsLoading(true);
          setCurrentPage(currentPage + 1);
        }}
      />
    );
  };

  const renderFilters = () => (
    <Row className="bookmarks-filters align-items-center mb-4 gx-3">
      <Col xs={12} md={6} className="mb-3 mb-md-0">
        <FormControl fullWidth size="small">
          <TextField
            id="id"
            className="w-100"
            label="Search bookmarks"
            variant={"outlined"}
            value={searchTerm}
            size="small"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search entities..."
            InputProps={{
              style: { fontSize: 14 },
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ style: { fontSize: 14 } }}
            owner="admin"
            sub_variant="presentation-filter"
          />
        </FormControl>
        {/* <TextField
          className="w-100"
          label="Search bookmarks"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applySearch();
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchTerm("");
                      setCurrentPage(0);
                      setQuery("");
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" onClick={applySearch}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        /> */}
      </Col>
      <Col xs={12} md={4}>
        <FormControl
          className="w-100"
          fullWidth
          size="small"
          sx={{ backgroundColor: "primary.light" }}
        >
          <InputLabel
            id="type-filter-label"
            sx={{
              fontSize: 14,
              color: "rgba(0, 0, 0, 0.87)",
            }}
            size="small"
          >
            Type
          </InputLabel>
          <Select
            labelId="type-filter-label"
            id="type-filter"
            value={selectedType}
            onChange={handleTypeChange}
            // input={<OutlinedInput label="Type" />}
            input={
                <OutlinedInput
                  owner="admin"
                  sub_variant="presentation-filter"
                  sx={{
                    fontSize: 14,
                  }}
                  label="Status"
                />
            }
          >
            <MenuItem value="all">All types</MenuItem>
            {TYPE_OPTIONS.map((option) => (
              <MenuItem key={`type-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <FormControl className="w-100" size="small">
          <InputLabel id="bookmark-type-label">Type</InputLabel>
          <Select
            labelId="bookmark-type-label"
            id="bookmark-type"
            value={selectedType}
            onChange={handleTypeChange}
            input={<OutlinedInput label="Type" />}
          >
            <MenuItem value="all">All types</MenuItem>
            {TYPE_OPTIONS.map((option) => (
              <MenuItem key={`type-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}
      </Col>
    </Row>
  );

  const renderBody = () => {
    if (isLoading) return <CompanyappLoader />;
    if (results.length === 0) return <NoData activeIndex={0} />;
    return (
      <div className="bookmarks-sections">
        {sections.map((section) => (
          <Accordion
            key={section.key}
            defaultExpanded
            disableGutters
            className="bookmark-accordion"
            elevation={0}
            sx={{
              boxShadow: "none",
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`bookmarks-${section.key}-content`}
              id={`bookmarks-${section.key}-header`}
              className="px-0 bookmark-section-summary"
            >
              <Typography
                owner="client"
                variant="h6"
                color="clientPrimary.main"
              >
                {section.title} ({section.items.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails className="px-0 bookmark-section-detail">
              <Row className="bookmarks-grid d-flex align-items-stretch">
                {section.items.map((item) => renderCard(item))}
              </Row>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    );
  };

  if (isError) {
    return <Error status={errorStatus} message={errorMessage} />;
  }

  return (
    <main className="bookmarks screen">
      <PageHeader pageName="Bookmarks" owner="client" xxlOverride={12} />
      <Row>
        <Container fluid className="bookmarks-container">
          {renderFilters()}
          {renderBody()}
          {renderPagination()}
        </Container>
      </Row>
    </main>
  );
}
